// The map route contract: helpers for a route stored inside a record's
// reserved free-form `fields` JSON blob (AWSJSON).
//
// This module is the single choke point for reading and writing that blob:
//
// - AWSJSON arrives from AppSync as a JSON *string*; nothing else in the app
//   parses it. `parseTripExtraFields` accepts string | object | null and
//   never throws.
// - AWSJSON writes replace the whole attribute, so a route save must merge
//   into the record's latest `fields` value rather than write `{route}` alone.
//   `mergeRouteIntoFields` replaces only the route key and round-trips any
//   other keys (future users of the blob) untouched. Concurrent edits to
//   different keys are last-writer-wins; acceptable at this scale.
//
// TWO RECORD TYPES, TWO KEYS. A trip's route lives at `fields.route`
// (TRIP_ROUTE_KEY, TaskPogoTrip). A logbook entry's lives at `fields.geoRoute`
// (ENTRY_ROUTE_KEY, TaskPogoCompletedTask) because the Pilot's Logbook field
// registry already owns `fields.route` as a free-text "Filed Route" string.
// So: NEVER call the default-key readers on a CompletedTask. Today a
// free-text string reads as "no route" and nothing breaks, but that is a
// coincidence of the validation, not a guarantee. One blob may legitimately
// hold both keys; every helper here only ever touches the key it is given.
//
// Route shape (v1):
//   { v: 1, waypoints: [{ id, kind: 'from'|'via'|'to', name, lat, lon,
//                         src: 'geocode'|'user', geocodedAt?, placeId?,
//                         stopover?, stopNote?, arriveAt?, departAt? }, ...] }
//
// The waypoint array is ordered (render order = route order): `from` first,
// `to` last, any number of `via` between. `src` records how the position was
// set -- once a user drags a marker it flips to 'user' and the point is never
// re-geocoded. Readers ignore unknown keys; an unknown `v` reads as "no
// route" rather than an error so an old client can't corrupt a newer blob.
//
// `placeId` binds the waypoint to a row in the shared gazetteer
// (TaskPogoPlace), which OWNS the position: see `resolveRouteAgainstPlaces`.
// The stored lat/lon stays as a last-known snapshot, so a client that can't
// reach the gazetteer -- or one predating this key, which drops it on read --
// still draws the route. That is why the version stays 1: the key is additive
// and losing it costs a binding, never a position.
//
// A waypoint may be UNPLACED: `lat` and `lon` are both null. It has a name and
// a position in the route order, but nothing on the map -- what a point becomes
// when the name its position was placed under changes (see `nameOwnsPosition`),
// and what the editor offers "Lookup place" / "Place on map" for. Everything
// geometric here (line/bounds/distance/insert) works off the PLACED points
// only, so an unplaced point in the middle of a route simply doesn't bend it.
// A client predating this reads such a waypoint as invalid and drops it -- it
// loses a name, not a position.
//
// A via may also be a ZERO-DAY STOPOVER -- a picnic stop, a fuel stop: a point
// you stop at without spending a night there. See the stopover section below
// for why it is a flag on a waypoint rather than a leg of its own.

import { EARTH_RADIUS_NM, haversineNm, unionBounds } from './GeoBounds.mjs';
import { normalizePlaceName, placeDisplayName } from './PlaceFields.mjs';

export const ROUTE_VERSION = 1;

// Where a route lives inside its record's `fields` blob. See the header: the
// two record types must use different keys, and every reader/writer below
// defaults to the trip's so no existing call site had to change.
export const TRIP_ROUTE_KEY = 'route';
export const ENTRY_ROUTE_KEY = 'geoRoute';

const WAYPOINT_KINDS = ['from', 'via', 'to'];

// The endpoint kinds, in route order. Anchors are the trip's From/To: exactly
// one of each at most, they don't reorder, and they can't be deleted.
export const ANCHOR_KINDS = ['from', 'to'];

// Everything the UI says about an endpoint, in one place so the waypoint list,
// the map card and the locator hook can't drift: the row label and empty-box
// prompt, the tap-to-place hint, how to refer to an endpoint the user hasn't
// named, and the button on the map's missing-anchor prompt.
//
// `noun` is lowercase because it reads mid-sentence in the geocode-miss notice.
// This lives beside `waypointLabel` (which already returns 'Start'/'End') so
// the strings sit with the model they describe -- and, just as importantly, in
// a module free of maplibre: the trip page imports it eagerly, and pulling it
// from the map card would drag the ~250 KB map chunk into the entry bundle.
export const ANCHOR_COPY = {
  from: {
    label: 'Start',
    placeholder: 'Starting point',
    hint: 'Tap the map to place the start point',
    noun: 'the starting point',
    button: 'Lookup Start',
  },
  to: {
    label: 'End',
    placeholder: 'Destination',
    hint: 'Tap the map to place the destination',
    noun: 'the destination',
    button: 'Lookup End',
  },
};

// AWSJSON blob (string | object | null) -> plain object. {} on anything
// unparseable so callers can always dot into the result.
export function parseTripExtraFields(fields) {
  if (fields == null) return {};
  let parsed = fields;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return {};
    }
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return {};
  }
  return parsed;
}

function isFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

function hasValidCoordinates(wp) {
  return (
    isFiniteNumber(wp.lat) && wp.lat >= -90 && wp.lat <= 90 &&
    isFiniteNumber(wp.lon) && wp.lon >= -180 && wp.lon <= 180
  );
}

// Is this waypoint on the map? An unplaced one carries a name and its place in
// the route order, but no position.
export function isPlaced(waypoint) {
  return waypoint != null && hasValidCoordinates(waypoint);
}

// A waypoint is readable when its kind is known and it is either properly
// placed or properly unplaced. Coordinates that are present but nonsense (out
// of range, NaN, half a pair) make the whole waypoint unreadable rather than
// silently unplaced -- a corrupt position must not masquerade as "the user
// hasn't located this yet".
function isValidWaypoint(wp) {
  if (wp == null || typeof wp !== 'object') return false;
  if (!WAYPOINT_KINDS.includes(wp.kind)) return false;
  if (wp.lat == null && wp.lon == null) return true;
  return hasValidCoordinates(wp);
}

// The waypoints that actually have a position, in route order. Every
// geometric helper below starts here.
export function placedWaypoints(route) {
  return (route?.waypoints || []).filter(isPlaced);
}

// Is any of this route on the map? A route of nothing but unplaced points
// draws no line, has no bounds and no length -- callers that gate on "is there
// a map to show" want this rather than `Boolean(route)`.
export function hasPlacedWaypoints(route) {
  return placedWaypoints(route).length > 0;
}

// Does this waypoint's position belong to its name? A named, placed point's
// position is a claim about that name -- whether the geocoder derived it or
// the user tapped it into place under the name -- so committing a different
// name invalidates it: the editors unplace the point on commit and offer
// "Lookup place" (or "Place on map") for the new name. The exemption is a
// point with no real name yet (an unnamed map tap, or an auto-generated
// coordinate name): there the first name is the naming gesture for a pin the
// user just placed, and unplacing would throw away the very tap that created
// it.
export function nameOwnsPosition(waypoint) {
  return (
    isPlaced(waypoint) &&
    !waypoint.autoName &&
    (waypoint.name || '').trim() !== ''
  );
}

// ---------------------------------------------------------------------------
// ZERO-DAY STOPOVERS
// ---------------------------------------------------------------------------
//
// A via may be marked `stopover: true`: a point the boat or aircraft actually
// STOPS at without spending a night there -- a picnic stop, a fuel stop.
//
// "Zero day" is literal, and it is the whole reason this lives on a WAYPOINT
// rather than being a leg of its own. A leg has no order column: its position
// within a trip IS its date, and a leg's `days` is what dates the next one
// (TripGroups' cascade; TripFields.legDays clamps to 1 for exactly this
// reason). A second leg sharing a date would leave the trip with no order, so
// a stop that costs no day cannot be a leg. As a flag on a via it costs the
// schedule nothing at all: the route still runs through the point, the leg's
// distance still measures around it, and no date moves.
//
// Three optional details ride with it, and NONE of them is meaningful without
// it -- the rule `mapLabel` follows beside `placeId`, applied by
// `stopoverFields` on read so a hand-edited import file cannot smuggle a stop
// note onto a point nobody stops at:
//
//   stopNote  -- free text ("fuel, 40 gal", "lunch on the beach")
//   arriveAt  -- local clock time, 'HH:MM'
//   departAt  -- local clock time, 'HH:MM'
//
// The times are a TIME OF DAY and never an instant. A waypoint has no date of
// its own -- the leg (or the logbook entry) carries that, and it moves every
// time the days chain cascades -- so an instant stored here would silently
// drift onto another day the first time the trip was re-dated.
//
// ANCHORS ARE NEVER STOPOVERS. `from` and `to` are where the leg starts and
// ends its day, which is the opposite claim; `isStopover` and the read
// whitelist both key on `kind === 'via'` rather than trusting the flag.
//
// Additive to route v1, exactly like `placeId`: a client predating this reads
// a stopover as an ordinary via, which loses an annotation and never a
// position.

// Long enough for "Fuel + customs, call ahead on 16"; short enough that the
// blob stays a blob. A leg's own Notes column is where a paragraph goes.
export const MAX_STOP_NOTE_LENGTH = 280;

// 'HH:MM' on a 24-hour clock -- what <input type="time"> emits.
const STOP_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

// A stored stop time, or null for anything that isn't one. A `step`-bearing
// time input emits 'HH:MM:SS', so seconds are dropped rather than rejected;
// everything else has to be a real clock reading, because the display helpers
// below hand it to a Date and "25:00" would become tomorrow.
export function normalizeStopTime(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const [hours, minutes] = trimmed.split(':');
  const hhmm = `${hours}:${minutes}`;
  return STOP_TIME_PATTERN.test(hhmm) ? hhmm : null;
}

// A stored stop note, or '' for anything that isn't one. Capped rather than
// rejected -- a long note is a user typing, not a corrupt blob.
//
// NOT trimmed. This runs on the write path too, where the box is controlled
// straight off the route: trimming here would eat the space in "fuel, " as
// fast as it was typed and the next keystroke would land against "fuel".
// Emptiness is asked as `.trim() !== ''` wherever it matters instead.
export function normalizeStopNote(value) {
  if (typeof value !== 'string') return '';
  return value.slice(0, MAX_STOP_NOTE_LENGTH);
}

// The detail keys that only exist beside the flag, named once so the setters
// below and the reader agree on what "the details" means.
const STOP_DETAIL_KEYS = ['stopNote', 'arriveAt', 'departAt'];

// The stopover keys a waypoint should carry, as an object to spread. Empty for
// anything that isn't a via marked as a stopover, which is what drops the
// details with the flag -- on read, and again every time the flag is cleared.
function stopoverFields(wp) {
  if (wp?.kind !== 'via' || wp.stopover !== true) return {};
  const note = normalizeStopNote(wp.stopNote);
  const arriveAt = normalizeStopTime(wp.arriveAt);
  const departAt = normalizeStopTime(wp.departAt);
  return {
    stopover: true,
    // Any non-empty string, NOT `note.trim()`. This runs on the write path, so
    // dropping a whitespace-only value would eat a LEADING space exactly as
    // trimming eats a trailing one: the box is controlled straight off the
    // route, so the first " " never lands and the next keystroke builds on an
    // empty string. Whether a note SAYS anything is a different question, asked
    // with `.trim()` at the one place that renders it (stopoverSummary).
    ...(note ? { stopNote: note } : {}),
    ...(arriveAt ? { arriveAt } : {}),
    ...(departAt ? { departAt } : {}),
  };
}

// Is this waypoint a zero-day stopover? Keyed on `kind` as well as the flag,
// so a flag that reached an anchor -- an import file, a route written by a
// client that let it -- is simply not one.
export function isStopover(waypoint) {
  return waypoint?.kind === 'via' && waypoint.stopover === true;
}

// The route's stopovers, in route order.
export function stopoverWaypoints(route) {
  return (route?.waypoints || []).filter(isStopover);
}

// A stored 'HH:MM' as the reader's own clock reads it -- "10:30 AM" in en-US,
// "10:30" in en-GB. '' for anything unusable.
//
// The Date is built in the LOCAL zone and read back in the local zone, so the
// clock reading round-trips exactly whatever the host's offset is; the only
// thing it is for is to reach Intl's 12-versus-24-hour decision. The date part
// is arbitrary and never shown.
export function formatStopTime(value) {
  const hhmm = normalizeStopTime(value);
  if (!hhmm) return '';
  const [hours, minutes] = hhmm.split(':').map(Number);
  return new Date(2000, 0, 1, hours, minutes).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
}

// A stopover's times as one phrase: "10:30 AM – 11:45 AM", or "from 10:30 AM"
// / "until 11:45 AM" when only one end is known, or '' when neither is.
//
// Half an answer is kept rather than dropped, for joinAnchorLabels' reason: a
// stop with only an arrival time is an ordinary thing to record, and saying
// nothing about it would read as a stop with no times at all.
export function stopoverTimes(waypoint) {
  if (!isStopover(waypoint)) return '';
  const arrive = formatStopTime(waypoint.arriveAt);
  const depart = formatStopTime(waypoint.departAt);
  if (arrive && depart) return `${arrive} – ${depart}`;
  if (arrive) return `from ${arrive}`;
  if (depart) return `until ${depart}`;
  return '';
}

// One line describing a stopover -- its times and its note, whichever exist --
// or '' when it carries neither. The single wording, so the waypoint row, the
// map dialog and any read-only surface cannot describe one stop three ways.
export function stopoverSummary(waypoint) {
  if (!isStopover(waypoint)) return '';
  const note = normalizeStopNote(waypoint.stopNote).trim();
  return [stopoverTimes(waypoint), note].filter(Boolean).join(' · ');
}

// Mark a via as a stopover, or stop being one.
//
// Clearing DROPS the details. Leaving them behind would make the in-memory
// route disagree with what the next load reads back (stopoverFields drops them
// on read), and a note the user watched disappear must not come back because
// they re-ticked the box.
//
// Anchors are ignored outright rather than silently flagged, the rule
// `removeWaypoint` follows for the same pair.
export function setStopover(route, id, stopover) {
  return {
    v: ROUTE_VERSION,
    waypoints: (route?.waypoints || []).map((wp) => {
      if (wp.id !== id || wp.kind !== 'via') return wp;
      const bare = { ...wp };
      delete bare.stopover;
      for (const key of STOP_DETAIL_KEYS) delete bare[key];
      return stopover ? { ...bare, ...stopoverFields({ ...wp, stopover: true }) } : bare;
    }),
  };
}

// Patch a stopover's details. An omitted (`undefined`) key is left alone; an
// empty string or null CLEARS that key, which is how a cleared time input and
// an untouched one are told apart.
//
// A no-op on a waypoint that isn't a stopover: the details are meaningless
// without the flag, so the caller sets the flag first.
export function setStopDetails(route, id, details = {}) {
  return {
    v: ROUTE_VERSION,
    waypoints: (route?.waypoints || []).map((wp) => {
      if (wp.id !== id || !isStopover(wp)) return wp;
      const merged = { ...wp };
      const bare = { ...wp };
      for (const key of STOP_DETAIL_KEYS) {
        if (details[key] !== undefined) merged[key] = details[key];
        delete bare[key];
      }
      return { ...bare, ...stopoverFields(merged) };
    }),
  };
}

// A route read out of a `fields` blob, normalized, or null when the blob has
// none under `key` (or it is malformed / from an unknown version). Malformed
// waypoints are dropped; waypoints with no coordinates are kept as unplaced
// (lat/lon null); `from` is moved first and `to` last (vias keep their relative
// order) so the rest of the app can trust the ordering.
export function routeFromFields(fields, key = TRIP_ROUTE_KEY) {
  const route = parseTripExtraFields(fields)[key];
  if (route == null || typeof route !== 'object' || Array.isArray(route)) return null;
  if (route.v !== ROUTE_VERSION) return null;
  if (!Array.isArray(route.waypoints)) return null;

  const waypoints = route.waypoints.filter(isValidWaypoint).map((wp) => ({
    id: typeof wp.id === 'string' && wp.id ? wp.id : makeWaypointId(),
    kind: wp.kind,
    name: typeof wp.name === 'string' ? wp.name : '',
    // Normalize "no position" to an explicit null pair, so nothing downstream
    // has to tell undefined from null from missing.
    lat: isPlaced(wp) ? wp.lat : null,
    lon: isPlaced(wp) ? wp.lon : null,
    src: wp.src === 'geocode' ? 'geocode' : 'user',
    // Only a string survives -- the blob can arrive from an imported file,
    // and this key must not carry arbitrary JSON.
    ...(typeof wp.geocodedAt === 'string' && wp.geocodedAt ? { geocodedAt: wp.geocodedAt } : {}),
    // Same rule, same reason: the gazetteer binding is an id, and an import
    // file is hand-editable. An id that doesn't resolve is harmless -- the
    // resolver falls back to the name -- so no further validation is useful.
    ...(typeof wp.placeId === 'string' && wp.placeId ? { placeId: wp.placeId } : {}),
    // The bound gazetteer row's short map label (placeDisplayName), when it
    // differs from the name. Like lat/lon/name it is a SNAPSHOT the overlay
    // refreshes -- the row owns it -- and like them it is read back rather
    // than recomputed because it has to survive a round trip: TripGroup and
    // LogbookTrip resolve a route and immediately re-serialize it into an
    // in-memory leg that TripGroupMap re-parses here. A key this dropped
    // would never reach the group map's markers.
    //
    // Only meaningful beside a placeId; waypointMapLabel enforces that, so an
    // import file can't smuggle a label onto a hand-typed waypoint.
    ...(typeof wp.mapLabel === 'string' && wp.mapLabel.trim() ? { mapLabel: wp.mapLabel } : {}),
    // A name the app generated from the coordinates rather than one the
    // user typed (see makeWaypoint / formatLatLon).
    ...(wp.autoName ? { autoName: true } : {}),
    // The zero-day stopover flag and its details, or nothing. stopoverFields
    // is the whitelist: it drops the lot on an anchor and drops the details on
    // a via that isn't flagged, so a hand-edited import file cannot annotate a
    // point nobody stops at.
    ...stopoverFields(wp),
  }));
  if (waypoints.length === 0) return null;

  const from = waypoints.filter((wp) => wp.kind === 'from');
  const vias = waypoints.filter((wp) => wp.kind === 'via');
  const to = waypoints.filter((wp) => wp.kind === 'to');
  return { v: ROUTE_VERSION, waypoints: [...from, ...vias, ...to] };
}

// The trip's route (TaskPogoTrip.fields.route).
export function routeFromTrip(trip, key = TRIP_ROUTE_KEY) {
  return routeFromFields(trip?.fields, key);
}

// A logbook entry's route (TaskPogoCompletedTask.fields.geoRoute). Separate
// named reader rather than a key argument at every call site, so the "which
// key does this record use" decision is made once, here.
//
// `jsonCache` is the entry's ALREADY-PARSED blob, the same optional argument
// readEntryField takes and for the same reason: `entry.fields` arrives from
// AppSync as a string, and a caller looping over a whole logbook has usually
// parsed it once already. Passing it avoids a second JSON.parse per entry --
// parseTripExtraFields accepts an object or a string, so the two are otherwise
// identical. Omit it and nothing changes.
export function routeFromEntry(entry, jsonCache) {
  return routeFromFields(jsonCache || entry?.fields, ENTRY_ROUTE_KEY);
}

// Fill blank anchor names from the record's `from` / `to` values. The trip
// detail page edits those columns through the route's Start/End rows, so an
// anchor that was never named -- an older trip, or one tapped onto the map
// before a From was typed -- would otherwise render an empty box beside a
// column that does have a value. Returns the SAME route when nothing needed
// filling, so seeding the page never reads as an edit.
export function backfillEndpointNames(route, { from, to } = {}) {
  if (route == null || !Array.isArray(route.waypoints)) return route;

  const nameFor = (kind) => (kind === 'from' ? from : to);
  const isBlank = (value) => value == null || String(value).trim() === '';

  let changed = false;
  const waypoints = route.waypoints.map((wp) => {
    if (wp.kind === 'via' || !isBlank(wp.name)) return wp;
    const name = nameFor(wp.kind);
    if (isBlank(name)) return wp;
    changed = true;
    return { ...wp, name: String(name) };
  });

  return changed ? { ...route, waypoints } : route;
}

// The `mapLabel` a waypoint bound to `place` should carry, or undefined.
//
// Carried only when it DIFFERS from the name the waypoint will have: most rows
// have no comma and no friendlyName, and attaching a key equal to what
// waypointLabel would say anyway would make every one of those waypoints read
// as CHANGED on first resolve, for nothing.
//
// One definition, two callers -- resolveWaypoint (the gazetteer overlay) and
// waypointFromPlace (a waypoint born by tapping a dot on the map). Two copies
// would drift the first time the friendlyName policy moved, and the symptom --
// a marker captioned differently depending on how its waypoint was created --
// is exactly the kind of thing nobody reports.
export function mapLabelForPlace(place, name) {
  const label = placeDisplayName(place);
  return label && label !== name ? label : undefined;
}

// One waypoint resolved against the gazetteer. See resolveRouteAgainstPlaces
// for the contract; this returns the SAME object when nothing changed.
function resolveWaypoint(wp, byId, byKey) {
  // An unplaced point has no position to correct, and re-placing it would undo
  // the rename that unplaced it -- putting it back on the map is the user's
  // gesture to make, not ours. An autoName point's name IS its coordinates, so there is
  // no place to look up. A blank name normalizes to a key nothing can match.
  if (!isPlaced(wp) || wp.autoName) return wp;
  const name = (wp.name || '').trim();
  if (!name) return wp;

  // The id is the durable binding and wins; the name is the bootstrap that
  // binds routes written before the binding existed.
  let row = (wp.placeId && byId.get(wp.placeId)) || byKey.get(normalizePlaceName(name)) || null;

  // A row whose position is nonsense is worse than no row: applying it would
  // make the whole waypoint unreadable to isValidWaypoint on the next read.
  if (row && !hasValidCoordinates(row)) row = null;

  if (!row) {
    // Rule 2: the bound row is gone (anyone may delete any row) and no name
    // match replaces it. Keep the snapshot -- a delete costs the binding, not
    // the position -- and drop the dangling id so a later row can bind it.
    if (!wp.placeId) return wp;
    // `mapLabel` goes with the id, not with the position: it is the ROW's
    // short name, and there is no longer a row. The snapshot name stays and
    // waypointMapLabel falls back to it.
    const { placeId, mapLabel, ...rest } = wp;
    return rest;
  }

  // The row owns the name as well as the position. Without this a renamed row
  // leaves the waypoint permanently mislabelled -- and worse, typing the row's
  // new name into the box would read as a DIVERGENT name and unplace a
  // correctly-bound point.
  const rowName = typeof row.name === 'string' && row.name.trim() ? row.name : name;

  // ...and the row owns the MAP LABEL too, so a friendlyName typed in
  // /me/places reaches every marker bound to it. The rule (and the reasoning
  // for "only when it differs") is mapLabelForPlace's, above.
  const nextLabel = mapLabelForPlace(row, rowName);

  // `src` is deliberately untouched: it records how the position was first
  // established, the row carries its own `source`, and rewriting it here would
  // dirty every waypoint on first resolve for no behavioural gain.
  if (
    wp.placeId === row.id &&
    wp.lat === row.lat &&
    wp.lon === row.lon &&
    wp.name === rowName &&
    wp.mapLabel === nextLabel
  ) {
    return wp;
  }
  const next = { ...wp, name: rowName, lat: row.lat, lon: row.lon, placeId: row.id };
  // Assign or DELETE -- the spread above carries a stale label forward, which
  // is exactly what happens when a row's friendlyName is cleared.
  if (nextLabel) next.mapLabel = nextLabel;
  else delete next.mapLabel;
  return next;
}

// Overlay the shared gazetteer onto a route: the place row OWNS the position,
// the route's stored lat/lon is a last-known snapshot. This is what makes
// moving a place in /me/places move every waypoint bound to it, for every user.
//
// `placeIndex` is { byId, byKey } from places.mjs `loadPlaceIndex` (byKey is
// already resolved through pickPlace, so duplicate names are decided in one
// place). Per waypoint:
//
//   1. placeId resolves        -> take the row's position, name AND map label
//   2. placeId dangles         -> try the name; else keep the snapshot and
//                                 drop the dead id (and its map label)
//   3. no placeId, name hits   -> take the position and ADOPT the row's id,
//                                 which is how legacy routes bind with no
//                                 migration
//   4. no match                -> untouched
//
// An EMPTY index returns the route unchanged rather than clearing bindings. A
// failed gazetteer load and a genuinely empty table are indistinguishable here,
// and stripping every placeId on a transient network failure would be a far
// worse outcome than skipping one overlay.
//
// Pure, and returns the SAME route object when nothing changed -- callers rely
// on that identity to tell "the gazetteer had nothing to say" from a real edit,
// so seeding a page never reads as a change the user made. Same contract as
// backfillEndpointNames.
export function resolveRouteAgainstPlaces(route, placeIndex) {
  if (route == null || !Array.isArray(route.waypoints)) return route;

  const byId = placeIndex?.byId;
  const byKey = placeIndex?.byKey;
  if (!(byId instanceof Map) || !(byKey instanceof Map)) return route;
  if (byId.size === 0 && byKey.size === 0) return route;

  let changed = false;
  const waypoints = route.waypoints.map((wp) => {
    const next = resolveWaypoint(wp, byId, byKey);
    if (next !== wp) changed = true;
    return next;
  });

  return changed ? { ...route, waypoints } : route;
}

// Where the NEXT leg of a trip starts: the previous leg's End, rebuilt as a
// `from` waypoint, or null when that leg never put its End on the map.
//
// A leg picks up where the last one left off -- the group page already copies
// the previous leg's `to` column into the new leg's `from` -- and the position
// is part of that: the point was located once already, so asking for "Locate on
// map" again to reproduce a pin the user just placed is busywork.
//
// Only the `to` ANCHOR counts. A placed via further along the route is
// somewhere the leg passed through, not where it ended, and starting the next
// leg there would draw a line the boat never sailed.
//
// Names come out of `backfillEndpointNames`, so an anchor that was never named
// carries the leg's `to` column -- the same name the trip page shows for it.
// Provenance rides along too (`src`/`geocodedAt`/`autoName`): the carried point
// is the same claim about the same name, and the copy shouldn't read as a
// hand-placed pin if the original was geocoded. So does `placeId` -- it is the
// same physical place, so the new leg's Start should track the gazetteer row
// the previous leg's End was bound to -- and its `mapLabel`, so the carried
// marker reads the same before the new leg's first resolve. The waypoint id is
// fresh -- it's a new waypoint in a new route.
export function startWaypointFromPreviousLeg(previousLeg) {
  const route = backfillEndpointNames(routeFromTrip(previousLeg), {
    from: previousLeg?.from,
    to: previousLeg?.to,
  });
  const end = (route?.waypoints || []).find((wp) => wp.kind === 'to');
  if (!isPlaced(end)) return null;
  return makeWaypoint({
    kind: 'from',
    name: end.name,
    lat: end.lat,
    lon: end.lon,
    src: end.src,
    geocodedAt: end.geocodedAt,
    autoName: end.autoName,
    placeId: end.placeId,
    mapLabel: end.mapLabel,
  });
}

// The anchor kinds with no point on the map yet, in route order. A route can
// be half-placed: "Lookup place" geocodes from and to independently, so a
// name it can't find leaves that anchor off the route while the other one
// lands -- and renaming a geocoded anchor unplaces it in situ. Both count as
// missing here. The trip page always renders both anchor rows, and the map card
// uses this to offer a way to place whichever is still missing.
export function missingAnchorKinds(route) {
  const waypoints = route?.waypoints || [];
  return ANCHOR_KINDS.filter(
    (kind) => !waypoints.some((wp) => wp.kind === kind && isPlaced(wp))
  );
}

// Add an anchor to the route, or replace the one already there. `from` stays
// first and `to` last; vias keep their order and are never touched. Returns a
// new route (a null/empty route becomes a one-waypoint route).
export function upsertAnchor(route, waypoint) {
  if (!ANCHOR_KINDS.includes(waypoint?.kind)) return route;

  const rest = (route?.waypoints || []).filter((wp) => wp.kind !== waypoint.kind);
  const vias = rest.filter((wp) => wp.kind === 'via');
  const from = waypoint.kind === 'from' ? [waypoint] : rest.filter((wp) => wp.kind === 'from');
  const to = waypoint.kind === 'to' ? [waypoint] : rest.filter((wp) => wp.kind === 'to');

  return { v: ROUTE_VERSION, waypoints: [...from, ...vias, ...to] };
}

// Parse the record's current `fields` (string | object | null) and set/replace
// only `key`. Pass route = null to remove it. Returns a NEW plain object ready
// for prepareTripInput (which stringifies for AWSJSON).
//
// Only `key` is touched, so a blob holding both a trip route and an entry
// route -- or any other key -- round-trips the rest untouched.
export function mergeRouteIntoFields(fields, route, key = TRIP_ROUTE_KEY) {
  const merged = { ...parseTripExtraFields(fields) };
  if (route == null) {
    delete merged[key];
  } else {
    merged[key] = route;
  }
  return merged;
}

// An anchor's name as a candidate for the record's `from` / `to` value, or
// undefined when it can't be one. `autoName` is the app's own rendering of the
// coordinates rather than a place name -- moveWaypoint rewrites it on every
// drag -- so it must never reach the column.
//
// Lives here rather than on a page because two editors now mirror anchor names
// outward (the trip's from/to COLUMNS, the logbook entry's from/to inside its
// blob) and the rule has to have one definition.
export function anchorName(route, kind) {
  const waypoint = (route?.waypoints || []).find((wp) => wp.kind === kind);
  if (!waypoint || waypoint.autoName) return undefined;
  return typeof waypoint.name === 'string' ? waypoint.name : undefined;
}

// The from/to values a route's anchors call for, skipping the ones that must
// leave the stored value alone: an anchor the route hasn't got, an auto-named
// one (anchorName's rule), and a BLANK one.
//
// The blank is where this deliberately differs from buildPayload, which does
// mirror an empty anchor name outward. That happens as part of a save the user
// caused, with the route as the agreed source of truth; a passive display
// overlay applying the same rule would blank a heading merely because someone
// opened the page. Only names the gazetteer or the user actually wrote get to
// replace what is stored.
function endpointNamesFromRoute(route, record) {
  const names = {};
  for (const kind of ANCHOR_KINDS) {
    const name = anchorName(route, kind);
    if (typeof name !== 'string' || name.trim() === '') continue;
    if (name === record?.[kind]) continue;
    names[kind] = name;
  }
  return names;
}

// A read-only DISPLAY copy of a record whose from/to mirror is re-derived from
// `route` -- the columns for a trip leg, the same keys inside the `fields` blob
// for a logbook entry.
//
// WHY a display overlay exists at all. That mirror is written from the route at
// SAVE time (see Trip.jsx's buildPayload), and `resolvePlaceName` can only find
// a gazetteer row whose name still MATCHES what was stored -- so a place
// renamed since the last save is reachable only through the waypoint's
// `placeId`, which lives in the route. Recovering it is this overlay's whole
// job, and why a page cannot get the right title from `resolvePlaceName` alone.
//
// The result carries FULL names, so the short-name rule composes on top: the
// value now names the row as the gazetteer currently spells it, which is
// exactly what lets `resolvePlaceName` inside tripTitle/placeCell find that row
// and shorten it for the surfaces that asked for short.
//
// DISPLAY ONLY -- none of this may reach a write path. Persisting the
// correction is the lazy write-back's job (Trip.jsx's routeGen), because
// opening a page must never be a mutation. Returns the SAME object when nothing
// changed, the identity contract resolveRouteAgainstPlaces holds and the memos
// downstream rely on.
//
// Take these two when the caller already HAS a resolved route (the two detail
// pages hold one in `routeDraft`, which also carries the user's unsaved
// renames, so their headings track the waypoint boxes as they're typed).
export function tripWithAnchorNames(trip, route) {
  return withAnchorNames(trip, route, TRIP_ROUTE_KEY);
}

export function entryWithAnchorNames(entry, route) {
  return withAnchorNames(entry, route, ENTRY_ROUTE_KEY);
}

// ...and these two when it has only the record and the gazetteer, which is
// every list, table and group heading. They resolve the record's own route
// first and carry it along in `fields`, so one call covers both what a map
// draws and what a title says.
export function tripWithResolvedEndpoints(trip, placeIndex) {
  return withResolvedEndpoints(trip, placeIndex, TRIP_ROUTE_KEY);
}

// The logbook entry's. Note the returned `fields` is a parsed plain object: it
// is a display value, so never hand it to splitEntryFields' `preserve` and
// never put it on a mutation input.
export function entryWithResolvedEndpoints(entry, placeIndex) {
  return withResolvedEndpoints(entry, placeIndex, ENTRY_ROUTE_KEY);
}

// A trip mirrors its anchors into columns; an entry mirrors them into the same
// blob the route sits in. Read the current values from wherever they live, and
// write them back there.
function withAnchorNames(record, route, key) {
  const inFields = key === ENTRY_ROUTE_KEY;
  const stored = inFields ? parseTripExtraFields(record?.fields) : record;
  const names = endpointNamesFromRoute(route, stored);
  if (Object.keys(names).length === 0) return record;
  return inFields
    ? { ...record, fields: { ...stored, ...names } }
    : { ...record, ...names };
}

function withResolvedEndpoints(record, placeIndex, key) {
  const base = routeFromFields(record?.fields, key);
  if (base == null) return record;
  const route = resolveRouteAgainstPlaces(base, placeIndex);
  const resolved =
    route === base
      ? record
      : { ...record, fields: mergeRouteIntoFields(record?.fields, route, key) };
  return withAnchorNames(resolved, route, key);
}

function makeWaypointId() {
  return 'wp_' + Math.random().toString(36).slice(2, 8);
}

// `autoName: true` marks a name the app generated from the coordinates
// (formatLatLon) rather than one the user typed. Such a name shows in the
// waypoint editor's name box -- so a new point reads as its position -- but
// map markers ignore it (see waypointLabel) and keep their compact numbered
// circle until the user types a real name. Dragging an auto-named point
// refreshes the text (see moveWaypoint).
export function makeWaypoint({
  kind,
  name = '',
  lat,
  lon,
  src = 'user',
  geocodedAt,
  autoName = false,
  placeId,
  mapLabel,
} = {}) {
  return {
    id: makeWaypointId(),
    kind,
    name,
    lat,
    lon,
    src,
    ...(geocodedAt ? { geocodedAt } : {}),
    ...(autoName ? { autoName: true } : {}),
    ...(placeId ? { placeId } : {}),
    // Only meaningful with a placeId -- it is the bound row's short name.
    ...(placeId && mapLabel ? { mapLabel } : {}),
  };
}

// Coordinates as degrees + decimal minutes ("48°30.8'N 122°36.2'W"), the
// format marine electronics and paper charts use. This is the default name
// a waypoint added from the editor gets.
export function formatLatLon(lat, lon) {
  const part = (value, positive, negative) => {
    const hemisphere = value >= 0 ? positive : negative;
    const absolute = Math.abs(value);
    let degrees = Math.floor(absolute);
    let minutes = (absolute - degrees) * 60;
    // Rounding to 1dp can tip 59.96' up to 60.0'; carry into the degrees.
    if (Math.round(minutes * 10) / 10 >= 60) {
      degrees += 1;
      minutes = 0;
    }
    return `${degrees}°${minutes.toFixed(1)}'${hemisphere}`;
  };
  return `${part(lat, 'N', 'S')} ${part(lon, 'E', 'W')}`;
}

// Does this name have formatLatLon's SHAPE? The `autoName` flag is the primary
// signal that a name is the app's own rather than the user's, but it is not
// reliable on old rows: a waypoint auto-named at one position and later dragged
// can be left carrying a coordinate string with no flag and coordinates the
// string no longer describes -- so comparing against formatLatLon(lat, lon)
// doesn't catch it either.
//
// Matching the format itself does, and safely: nothing anyone would type as a
// place name is degrees-decimal-minutes for both axes with hemispheres. This
// deliberately does NOT accept the other coordinate notations a person might
// write by hand -- the claim is only "the app generated this".
export function looksLikeCoordinateName(name) {
  return /^\d{1,3}°\d{1,2}\.\d'[NS] \d{1,3}°\d{1,2}\.\d'[EW]$/.test((name || '').trim());
}

// Marker label: the waypoint's name when the USER gave it one; otherwise
// vias show their 1-based position among the vias, and from/to fall back to
// Start/End. Auto-generated coordinate names are deliberately ignored here
// so the map doesn't fill up with wide coordinate pills.
export function waypointLabel(route, waypoint) {
  const name = waypoint?.autoName ? '' : (waypoint?.name || '').trim();
  if (name) return name;
  if (waypoint?.kind === 'from') return 'Start';
  if (waypoint?.kind === 'to') return 'End';
  const vias = (route?.waypoints || []).filter((wp) => wp.kind === 'via');
  const index = vias.findIndex((wp) => wp.id === waypoint?.id);
  return String(index >= 0 ? index + 1 : vias.length + 1);
}

// The label a MAP MARKER draws. Identical to waypointLabel except that a
// waypoint bound to a gazetteer row shows that row's short name -- its
// friendlyName, or its name up to the first comma (placeDisplayName, carried
// here as `mapLabel` by resolveRouteAgainstPlaces).
//
// Separate from waypointLabel rather than folded into it because the two answer
// different questions, and the dividing line is how much room the surface has:
// a marker, a table cell or a summary row gets the short name; a surface with
// room to be exact -- the waypoint modal title, RouteSummaryCard,
// useMapWeather's outlier message, a detail page, a delete confirmation -- says
// the full one, where the qualifier is what disambiguates. waypointLabel is the
// second of those. (Read-only From/To COLUMNS take the short name too, but by a
// different route: they have no waypoint to carry a mapLabel, so they resolve
// their stored string through PlaceFields' resolvePlaceName.)
//
// The placeId gate is what keeps this to gazetteer-backed names. A hand-typed
// "Ganges, Salt Spring" is shown as typed, and a `mapLabel` smuggled into a
// hand-edited import file has no row behind it and is ignored.
export function waypointMapLabel(route, waypoint) {
  if (waypoint?.placeId && !waypoint?.autoName) {
    const label = (waypoint.mapLabel || '').trim();
    if (label) return label;
  }
  return waypointLabel(route, waypoint);
}

// GeoJSON-ordered coordinates ([lon, lat] pairs) for the route line. Unplaced
// waypoints contribute nothing -- the line runs straight between the placed
// points either side of them.
export function routeLineCoordinates(route) {
  return placedWaypoints(route).map((wp) => [wp.lon, wp.lat]);
}

// [[minLon, minLat], [maxLon, maxLat]] (the corner order maplibre's
// fitBounds takes), or null for an empty route.
export function routeBounds(route) {
  const coords = routeLineCoordinates(route);
  if (coords.length === 0) return null;
  let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity;
  for (const [lon, lat] of coords) {
    if (lon < minLon) minLon = lon;
    if (lat < minLat) minLat = lat;
    if (lon > maxLon) maxLon = lon;
    if (lat > maxLat) maxLat = lat;
  }
  return [[minLon, minLat], [maxLon, maxLat]];
}

// Bounds of several routes together, or null when none of them is on the map.
export function combinedRouteBounds(routes) {
  return unionBounds((routes || []).map(routeBounds));
}

// The records that have something to draw, each paired with its parsed route
// (endpoint names backfilled from its From/To, as the trip page does). Input
// order is kept; records with no placed waypoints drop out. This is the group
// map's model: every leg it renders comes from here.
//
// `key` selects the blob key, so the same map can draw logbook entries: pass
// ENTRY_ROUTE_KEY and rows shaped { id, from, to, fields }.
export function tripsWithPlacedRoutes(trips, { key = TRIP_ROUTE_KEY } = {}) {
  return (trips || [])
    .map((trip) => ({
      trip,
      route: backfillEndpointNames(routeFromFields(trip?.fields, key), {
        from: trip?.from,
        to: trip?.to,
      }),
    }))
    .filter(({ route }) => hasPlacedWaypoints(route));
}

// Total route length in nautical miles, strung along the PLACED waypoints in
// order (from -> vias -> to). Null with fewer than 2 of them -- which is also
// the trip page's signal to blank the Distance field.
export function routeDistanceNm(route) {
  const waypoints = placedWaypoints(route);
  if (waypoints.length < 2) return null;
  let total = 0;
  for (let i = 0; i < waypoints.length - 1; i++) {
    total += haversineNm(waypoints[i], waypoints[i + 1]);
  }
  return total;
}

// A waypoint has to be this far from every other one before it can be called
// an outlier at all, however tight the rest of the route is -- without a floor
// the 5x test would flag the one open-water leg of an otherwise marina-hopping
// route.
const OUTLIER_FLOOR_NM = 500;
const OUTLIER_RATIO = 5;

function median(values) {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[middle - 1] + sorted[middle]) / 2
    : sorted[middle];
}

// The waypoint that looks misplaced -- almost always a geocoder hit that
// landed on a same-named place in the wrong country -- or null when the route
// looks coherent. Used to explain why the map turned weather off (a route
// stretched across the planet covers too large an area to query), so it names
// the cause rather than just the symptom.
//
// The test is a waypoint's distance to its NEAREST neighbour, compared against
// the median of that same measure. Nearest-neighbour rather than distance from
// the route's centre, because a legitimately long route (Seattle - Honolulu -
// Tokyo) has every point far from the centre while each still has a plausible
// neighbour; and median rather than mean, because a single bad point drags a
// mean far enough to hide itself. Under three PLACED waypoints there is
// nothing to compare -- with two you cannot tell which of them is the wrong
// one, and an unplaced point has no position to be wrong about.
export function routeOutlierWaypoint(route) {
  const waypoints = placedWaypoints(route);
  if (waypoints.length < 3) return null;

  const nearest = waypoints.map((wp, index) => {
    let best = Infinity;
    for (let i = 0; i < waypoints.length; i++) {
      if (i === index) continue;
      const d = haversineNm(wp, waypoints[i]);
      if (d < best) best = d;
    }
    return best;
  });

  const threshold = Math.max(OUTLIER_FLOOR_NM, OUTLIER_RATIO * median(nearest));

  let worst = -1;
  let worstDistance = threshold;
  nearest.forEach((distance, index) => {
    if (distance > worstDistance) {
      worstDistance = distance;
      worst = index;
    }
  });

  return worst < 0 ? null : { waypoint: waypoints[worst], nearestNm: nearest[worst] };
}

// Derived from the same sphere haversineNm measures on rather than written as
// the definitional 60, so a tolerance produced by one is comparable with a
// distance produced by the other.
const NM_PER_DEGREE = (Math.PI / 180) * EARTH_RADIUS_NM;

// Distance in NM from point p to segment a-b, in a locally-flat projection
// (degrees scaled to NM at p's latitude -- plenty accurate at trip scale for
// picking a segment and for comparing against a tolerance; never used for a
// real leg distance, which is haversineNm). `t` is clamped, so this is the
// distance to the SEGMENT, not to the infinite line through it.
function segmentDistanceNm(p, a, b) {
  const scale = NM_PER_DEGREE * Math.cos((p.lat * Math.PI) / 180);
  const px = p.lon * scale, py = p.lat * NM_PER_DEGREE;
  const ax = a.lon * scale, ay = a.lat * NM_PER_DEGREE;
  const bx = b.lon * scale, by = b.lat * NM_PER_DEGREE;
  const dx = bx - ax, dy = by - ay;
  const lengthSq = dx * dx + dy * dy;
  let t = lengthSq === 0 ? 0 : ((px - ax) * dx + (py - ay) * dy) / lengthSq;
  t = Math.max(0, Math.min(1, t));
  const cx = ax + t * dx, cy = ay + t * dy;
  return Math.hypot(px - cx, py - cy);
}

// Shortest distance in NM from `point` to the route's drawn line: the minimum
// over every segment between consecutive PLACED waypoints, so unplaced rows in
// between are stepped over rather than treated as a detour to the origin.
//
// A route with one placed point has no segment, so it measures to that point;
// a route with none has no answer at all and returns null (never 0, which a
// caller comparing against a tolerance would read as "right on top of it").
//
// Same locally-flat projection as segmentDistanceNm, and for the same reason:
// this exists to be compared against a tolerance ("is this tide station within
// 5 nm of where the boat goes"), not to be reported as a distance.
export function routeDistanceToPointNm(route, point) {
  const lat = Number(point?.lat);
  const lon = Number(point?.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

  const placed = placedWaypoints(route);
  if (placed.length === 0) return null;

  const p = { lat, lon };
  if (placed.length === 1) return segmentDistanceNm(p, placed[0], placed[0]);

  let best = Infinity;
  for (let i = 0; i < placed.length - 1; i++) {
    const d = segmentDistanceNm(p, placed[i], placed[i + 1]);
    if (d < best) best = d;
  }
  return best;
}

// A route's start and end MARKS: the placed waypoints whose `kind` is `from`
// and `to`. `kind` decides, never position -- an unplaced `from` leaves the
// route with no start rather than promoting the first placed via into one,
// which is the same rule anchorName applies at the other end of the model.
//
// A tide is read where the boat stops, which is why the weather email measures
// to these and not to the drawn line routeDistanceToPointNm answers for.
export function routeAnchors(route) {
  let start = null;
  let end = null;
  for (const waypoint of placedWaypoints(route)) {
    if (waypoint.kind === 'from' && !start) start = waypoint;
    if (waypoint.kind === 'to') end = waypoint;
  }
  return { start, end };
}

// What to CALL an anchor in a sentence about it: '8 nm from Sidney'.
//
// The gazetteer's short name when the anchor is bound to a row, else the name
// the user typed, else the positional word -- waypointMapLabel's ladder, which
// is the same one a marker pill climbs. `null` for the route: an anchor's
// `kind` short-circuits ahead of waypointLabel's via-index branch, so there is
// no route to consult.
//
// 'Start' and 'End' name nothing on their own -- a reader already knows which
// end of the leg they are holding -- so they are the FALLBACK here rather than
// the answer. Exported because the email's tide rows label their own endpoint
// (legEndpointStations) and must reach the same word for the same anchor; two
// copies would drift the first time the friendlyName policy moved, and one
// place called two things on one itinerary row is the kind of thing nobody
// reports.
//
// The coordinate guard is what looksLikeCoordinateName exists for: waypointLabel
// only tests the `autoName` flag, and a row auto-named before that flag existed
// carries a bare coordinate string with nothing marking it as the app's own --
// and one of those reads far worse than 'Start' in this sentence. Applied to
// the OUTPUT so it covers a mapLabel smuggled in the same shape, the way
// isPlaceCandidate guards the same case at the import end.
//
// The fallback is looked up rather than indexed because the positional word is
// the one thing only an ANCHOR has: both callers pass routeAnchors' pair, but
// this is exported, and a via handed in here has no Start/End to swap in. It
// keeps its own label in that case -- a coordinate string is a poor mark name
// and an empty one is worse, since formatAwayFrom drops the whole "from ..."
// clause for a blank label and the row would silently stop saying where the
// station is.
export function anchorMarkLabel(waypoint) {
  const label = waypointMapLabel(null, waypoint);
  const positional = ANCHOR_COPY[waypoint?.kind]?.label;
  return positional && looksLikeCoordinateName(label) ? positional : label;
}

// The two anchor labels as one phrase, for a row that speaks for BOTH ends --
// the email's deduped tide row, where one gauge is the nearest to a leg's start
// and its destination alike.
//
// Compared through normalizePlaceName rather than `===`: the two labels come
// from two different waypoints, which can spell one gazetteer row with
// different case or spacing, and "Sidney and Sidney" is the failure this
// collapse exists to prevent. The kept spelling is the START's -- a leg is read
// in that direction.
//
// With neither anchor named both sides fall back and this says "Start and End",
// exactly what it always said. With only one named it says "Sidney and End",
// which is deliberate: dropping the one name there is would make the merged row
// disagree with the two unmerged rows the same leg prints when no gauge is
// shared, and a reader is better served by the half of the answer that exists.
export function joinAnchorLabels(startLabel, endLabel) {
  if (!startLabel) return endLabel || '';
  if (!endLabel) return startLabel;
  if (normalizePlaceName(startLabel) === normalizePlaceName(endLabel)) return startLabel;
  return `${startLabel} and ${endLabel}`;
}

function markList(start, end) {
  const points = [];
  if (start) points.push({ label: anchorMarkLabel(start), lat: start.lat, lon: start.lon });
  if (end) points.push({ label: anchorMarkLabel(end), lat: end.lat, lon: end.lon });
  return points;
}

// The two marks as a flat list a distance can be measured against, each
// carrying the word the rest of the app calls it by. Unplaced anchors are
// simply absent, so a caller never has to test for them.
export function anchorPoints(route) {
  const { start, end } = routeAnchors(route);
  return markList(start, end);
}

// The same list for a RUN of legs read as one journey: the first route's start
// and the last one's end, which are the marks a whole trip begins and ends at.
// Each is taken from the first (last) route that actually has one, so a leg
// with an unplaced anchor hands the question on rather than dropping the mark.
//
// Only meaningful for legs that chain end to end. A caller drawing unrelated
// trips on one map has no such pair and must not ask.
export function routeSetAnchors(routes) {
  const list = Array.isArray(routes) ? routes : [];
  let start = null;
  let end = null;
  for (let i = 0; i < list.length && !start; i++) start = routeAnchors(list[i]).start;
  for (let i = list.length - 1; i >= 0 && !end; i--) end = routeAnchors(list[i]).end;
  return markList(start, end);
}

// Which of a route's marks a point is nearest, and how far off it is:
// `{ label, nm }`, or null when there is no mark or no position to measure.
// `marks` is anchorPoints'/routeSetAnchors' list, not routeAnchors' pair.
//
// haversineNm, not segmentDistanceNm: this number is REPORTED to a reader
// rather than compared against a tolerance, and the flat projection exists for
// the other job. Answering with the nearer of the two -- and saying which --
// is what routeDistanceToAnchorNm could not do, and why it was deleted: a bare
// minimum over both anchors leaves a reader unable to place the station at all.
export function nearestAnchorAway(marks, point) {
  // isPlaced on BOTH sides, never Number(): an unplaced waypoint carries
  // `lat: null`, and Number(null) is 0 -- a finite coordinate in the Gulf of
  // Guinea. Every station would have measured 6,000 nm to a mark that isn't
  // on the map at all.
  if (!isPlaced(point)) return null;

  let best = null;
  for (const mark of marks || []) {
    if (!isPlaced(mark)) continue;
    const nm = haversineNm(mark, point);
    if (!Number.isFinite(nm)) continue;
    if (best == null || nm < best.nm) best = { label: mark.label, nm };
  }
  return best;
}

// How far off a station is, in whole miles. What makes the weather email's
// loose 25 nm tide ceiling honest, and what places a row in the trip page's
// weather card, whose stations are ranked against the VIEWPORT's centre and so
// carry nothing else saying where they are. Anything under a mile is `<1 nm`
// rather than `0 nm`, which would claim more precision than "the nearest
// gauge" is offering.
export function formatAwayNm(nm) {
  if (!Number.isFinite(nm)) return '';
  return nm < 1 ? '<1 nm' : `${Math.round(nm)} nm`;
}

// The same distance with the mark it was measured to: '4 nm from Start'. Used
// wherever the row does not already name that mark -- the weather card's tide
// and current rows, and the email's currents. The email's TIDES are the one
// surface that doesn't want it: they are labelled `Tides · Start` already, and
// repeating the word in the same line would read as two different claims.
export function formatAwayFrom(nm, label) {
  const text = formatAwayNm(nm);
  if (!text || !label) return text;
  return `${text} from ${label}`;
}

// Where an APPENDED via goes: last in the list, but never after `to`.
function appendViaIndex(waypoints) {
  const toIndex = waypoints.findIndex((wp) => wp.kind === 'to');
  return toIndex < 0 ? waypoints.length : toIndex;
}

// Insert a new unnamed `via` waypoint at (lat, lon). Returns a NEW route; the
// input is not mutated.
//
// Two different gestures arrive here, and `snapNm` is the only thing that
// tells them apart. A tap that lands ON the drawn route line is a REFINEMENT:
// the new point belongs in the gap it was dropped into, which is what makes
// "tap between two markers to put one between them" work. A tap that lands off
// the line is an EXTENSION -- the user is laying the route out point by point,
// and the only answer that respects the order they clicked in is to append.
//
// Geometry alone cannot separate those, and it is not close: over random
// routes with Start, one via and End all placed, the nearest-segment rule puts
// a second tap BEFORE the first via about 55% of the time. (Cheapest-detour
// insertion -- the obvious alternative metric -- gives 50%. The metric is not
// the problem; having no notion of tap order is.) That reorder is not cosmetic
// either: an unnamed via is labelled by its POSITION among the vias (see
// waypointLabel), so the new point takes the old one's number and the old one
// takes the next, which on screen is indistinguishable from the two waypoints
// swapping places.
//
// `snapNm` is a distance but it stands for a distance on SCREEN: the map card
// passes the width of a couple dozen pixels at the tap, so "on the line" means
// what it looks like at the current zoom. Omitting it means "always insert",
// which is both the old behaviour and what every non-map caller wants.
export function insertViaNearSegment(route, lat, lon, { snapNm = Infinity } = {}) {
  return spliceVia(route, makeWaypoint({ kind: 'via', lat, lon, src: 'user' }), snapNm);
}

// Splice a ready-made via into `route` at the position its OWN coordinates ask
// for -- the refine-or-extend rule above, applied to a waypoint somebody else
// built. Extracted so the place-dot version (insertViaAtPlace, below) reuses
// that rule verbatim rather than restating it: the two gestures differ only in
// what waypoint they make, never in where it goes.
function spliceVia(route, via, snapNm) {
  const waypoints = route?.waypoints || [];
  const placed = placedWaypoints(route);

  const point = { lat: via.lat, lon: via.lon };
  let bestSegment = -1;
  let bestDistance = Infinity;
  for (let i = 0; i < placed.length - 1; i++) {
    const d = segmentDistanceNm(point, placed[i], placed[i + 1]);
    if (d < bestDistance) {
      bestDistance = d;
      bestSegment = i;
    }
  }

  // No segment at all (fewer than two placed points), or a tap that missed
  // every one of them: append. Segments run between PLACED points, so a tap
  // that hit one lands the new via just after the point that starts it; any
  // unplaced rows in between keep their place in the list.
  const insertAt = bestSegment < 0 || bestDistance > snapNm
    ? appendViaIndex(waypoints)
    : waypoints.indexOf(placed[bestSegment]) + 1;

  const next = [...waypoints];
  next.splice(insertAt, 0, via);
  return { v: ROUTE_VERSION, waypoints: next };
}

// Insert a new via at the MIDPOINT of the leg that starts at `id`, spliced
// directly after it -- the waypoint editor's "insert below" action, which
// has no map click to take a position from. No-ops when `id` isn't in the
// route, or when there's no leg below it to interpolate along: the midpoint
// needs `id` itself and the next PLACED waypoint after it to both have
// positions (the list hides the button in exactly those cases). The new point
// is auto-named with its coordinates.
export function insertViaAfter(route, id) {
  const waypoints = route?.waypoints || [];
  const index = waypoints.findIndex((wp) => wp.id === id);
  if (index < 0) return route;

  const a = waypoints[index];
  const b = waypoints.slice(index + 1).find(isPlaced);
  if (!isPlaced(a) || !b) return route;
  // Arithmetic midpoint: at trip scale the great-circle difference is far
  // below the precision a user would notice, and they can drag to adjust.
  const lat = (a.lat + b.lat) / 2;
  const lon = (a.lon + b.lon) / 2;
  const via = makeWaypoint({
    kind: 'via',
    name: formatLatLon(lat, lon),
    lat,
    lon,
    src: 'user',
    autoName: true,
  });

  const next = [...waypoints];
  next.splice(index + 1, 0, via);
  return { v: ROUTE_VERSION, waypoints: next };
}

// Move a VIA one place earlier (delta -1) or later (delta +1) among the
// vias. Anchors don't move and vias never cross them: routeFromTrip
// re-sorts to [from, ...vias, to] on load, so an ordering that crossed an
// anchor would silently revert after a save. Returns the route unchanged
// when the move isn't possible.
export function moveWaypointOrder(route, id, delta) {
  const waypoints = route?.waypoints || [];
  const index = waypoints.findIndex((wp) => wp.id === id);
  if (index < 0 || waypoints[index].kind !== 'via') return route;

  const target = index + (delta < 0 ? -1 : 1);
  if (target < 0 || target >= waypoints.length) return route;
  if (waypoints[target].kind !== 'via') return route;

  const next = [...waypoints];
  [next[index], next[target]] = [next[target], next[index]];
  return { v: ROUTE_VERSION, waypoints: next };
}

// Route edits used by the map UI. All return a NEW route.

// Give an existing waypoint a position -- a marker drag, or a geocode landing
// on a point that was named but not placed. `src`/`geocodedAt` record where the
// position came from (provenance only -- whether a later rename invalidates it
// is nameOwnsPosition, which keys on the name, not on `src`).
//
// `placeId` is REPLACED, not merged, and its absence is meaningful: a position
// that didn't come from a gazetteer row (a marker drag, a bare Photon hit)
// leaves the waypoint unbound, so the next resolve is free to bind it by name
// rather than keeping it pinned to a row it no longer sits on. Callers that
// mean "same binding, new position" pass the existing id back in -- which is
// exactly what the rename stash-and-restore in TripWaypoints does.
export function placeWaypoint(route, id, { lat, lon, src = 'user', geocodedAt, placeId } = {}) {
  return {
    v: ROUTE_VERSION,
    waypoints: (route?.waypoints || []).map((wp) => {
      if (wp.id !== id) return wp;
      // `mapLabel` goes out with `placeId` for the same reason: it is the
      // bound row's short name, and this call decides afresh which row (if
      // any) the point is bound to. Callers passing the SAME id back get the
      // label reattached by the next resolve; there is nothing to preserve by
      // hand, and a survivor would caption the new position with the old row.
      const {
        geocodedAt: previousGeocodedAt,
        placeId: previousPlaceId,
        mapLabel: previousMapLabel,
        ...rest
      } = wp;
      return {
        ...rest,
        lat,
        lon,
        src,
        ...(geocodedAt ? { geocodedAt } : {}),
        ...(placeId ? { placeId } : {}),
        // An auto-generated name IS the coordinates, so keep it honest when
        // the point moves.
        ...(wp.autoName ? { name: formatLatLon(lat, lon) } : {}),
      };
    }),
  };
}

// A marker drag: the user's own hand, so the position is authoritative.
//
// The gazetteer binding SURVIVES the drag. The drag is a correction to the
// bound row, not a detachment from it (TripRouteMap hands the id to
// recordManualPlacement, which updates that row rather than guessing by name),
// so the point stays bound and the row it is bound to moves under it. Dropping
// the binding here would work by accident -- the next resolve would re-bind by
// name to the row the drag just corrected -- but it would break the moment two
// rows share a name, which is a state pickPlace explicitly tolerates.
export function moveWaypoint(route, id, lat, lon) {
  const current = (route?.waypoints || []).find((wp) => wp.id === id);
  return placeWaypoint(route, id, { lat, lon, src: 'user', placeId: current?.placeId });
}

// Take a waypoint off the map, keeping its name and its place in the route.
// The editors do this when a rename invalidates a placed position
// (nameOwnsPosition) and then offer to place the new name. An
// auto-name was the old coordinates, so it goes with them.
//
// The gazetteer binding goes too. This is the one place it MUST: a waypoint is
// unplaced because it is no longer a claim about that place, and a surviving
// placeId would have the resolver put the old row's pin straight back -- the
// rename would look like it silently failed.
export function unplaceWaypoint(route, id) {
  return {
    v: ROUTE_VERSION,
    waypoints: (route?.waypoints || []).map((wp) => {
      if (wp.id !== id) return wp;
      const { geocodedAt, autoName, placeId, mapLabel, ...rest } = wp;
      return { ...rest, lat: null, lon: null, src: 'user' };
    }),
  };
}

// A name the user typed is no longer auto-generated; clearing the box back
// to empty hands the waypoint back to the default labeling (numbered vias).
export function renameWaypoint(route, id, name) {
  return {
    v: ROUTE_VERSION,
    waypoints: (route?.waypoints || []).map((wp) => {
      if (wp.id !== id) return wp;
      const { autoName, ...rest } = wp;
      return { ...rest, name: name || '' };
    }),
  };
}

// Only vias are removable; from/to are re-locatable, not deletable.
export function removeWaypoint(route, id) {
  return {
    v: ROUTE_VERSION,
    waypoints: (route?.waypoints || []).filter(
      (wp) => wp.id !== id || wp.kind !== 'via'
    ),
  };
}

// --- Picking a place off the map -------------------------------------------
//
// Every route edit below starts from a gazetteer ROW rather than from a
// coordinate: the trip map draws the shared place database as a dot layer, and
// tapping a dot is a claim about that place, not about the pixel under the
// finger. So the waypoint that results is BOUND -- it carries the row's
// `placeId`, and resolveRouteAgainstPlaces will keep taking its position, name
// and map label from the row on every read. That is the whole difference from
// tap-to-place, which mints a snapshot of wherever the tap landed.

// A brand-new waypoint standing for a gazetteer row.
//
// The name is the row's, VERBATIM -- not trimmed, and never the trip's From/To
// column. resolveWaypoint's identity fast-path compares against `row.name`
// unchanged, so normalizing here would make the very next resolve report a
// change and start the route a generation ahead of the server: "opening a page
// must never schedule a save", broken by a .trim().
//
// `src: 'user'` because a person picked it, and deliberately NO `geocodedAt`:
// nameOwnsPosition keys on the name, so a later rename should unplace this
// exactly as it unplaces a hand-placed pin.
export function waypointFromPlace(kind, place) {
  const name = typeof place?.name === 'string' ? place.name : '';
  return makeWaypoint({
    kind,
    name,
    lat: place?.lat,
    lon: place?.lon,
    src: 'user',
    placeId: place?.id,
    mapLabel: mapLabelForPlace(place, name),
  });
}

// Point an EXISTING waypoint at a gazetteer row: position, name, binding and
// map label, in one write.
//
// Deliberately not renameWaypoint + placeWaypoint. Two calls would be two route
// generations for one gesture, and worse, they leave an intermediate state in
// which the name has changed but the position hasn't -- precisely what
// nameOwnsPosition exists to invalidate. Here the name and the position arrive
// together, off the same row, so there is nothing to invalidate.
//
// `autoName` and `geocodedAt` go: this is a real name now, and the position did
// not come from a geocoder. The waypoint keeps its id and its place in the
// list -- this rebinds a point, it doesn't replace one.
export function bindWaypointToPlace(route, id, place) {
  const name = typeof place?.name === 'string' ? place.name : '';
  const mapLabel = mapLabelForPlace(place, name);
  return {
    v: ROUTE_VERSION,
    waypoints: (route?.waypoints || []).map((wp) => {
      if (wp.id !== id) return wp;
      // Assign or DROP, never carry forward: the spread would keep the label
      // and the binding of whatever row this point used to stand for.
      const { geocodedAt, autoName, placeId, mapLabel: previousLabel, ...rest } = wp;
      return {
        ...rest,
        name,
        lat: place?.lat,
        lon: place?.lon,
        src: 'user',
        ...(place?.id ? { placeId: place.id } : {}),
        ...(place?.id && mapLabel ? { mapLabel } : {}),
      };
    }),
  };
}

// Set an anchor from a gazetteer row: rebind the one already in the route, or
// create it. One function because its two callers -- the map popup's "Set as
// start" and an armed placement whose tap landed on a dot -- must not be able
// to disagree about which case they are in.
export function setAnchorFromPlace(route, kind, place) {
  if (!ANCHOR_KINDS.includes(kind)) return route;
  const existing = (route?.waypoints || []).find((wp) => wp.kind === kind);
  return existing
    ? bindWaypointToPlace(route, existing.id, place)
    : upsertAnchor(route, waypointFromPlace(kind, place));
}

// Set an anchor from an EXISTING waypoint's identity -- the waypoint dialog's
// "Set as destination" on the Start marker (and the reverse), which is the only
// way to start and end a trip at one place from the map: the anchor's marker
// sits on top of the gazetteer dot and swallows the tap, so the dot popup's own
// anchor actions can never be reached again.
//
// The other anchor becomes the same claim about the same point: name VERBATIM
// (waypointFromPlace's rule -- a normalized copy would make the very next
// resolve report a change), position, and the gazetteer binding (placeId +
// mapLabel) when there is one. Provenance rides along (src/geocodedAt/
// autoName), startWaypointFromPreviousLeg's rule: the copy shouldn't read as a
// hand-placed pin if the original was geocoded.
//
// NOT carried: `kind` (the target's), `id` (the target anchor keeps its own --
// this rebinds an anchor, it doesn't replace one), and the stopover keys
// (anchors are never stopovers; makeWaypoint's whitelist drops them).
//
// No-ops return the SAME route object, which is also how the dialog decides
// whether to offer the button at all: kind isn't an anchor; no source; the
// source already IS that kind; the source carries no identity (unplaced,
// unnamed, unbound); or the target anchor already stands for the same identity
// -- same binding when either side is bound, else same verbatim name and
// position.
export function setAnchorFromWaypoint(route, kind, source) {
  if (!ANCHOR_KINDS.includes(kind) || source == null) return route;
  if (source.kind === kind) return route;
  const named = (source.name || '').trim() !== '';
  if (!isPlaced(source) && !named && !source.placeId) return route;

  const existing = (route?.waypoints || []).find((wp) => wp.kind === kind);
  if (existing) {
    const same = source.placeId || existing.placeId
      ? existing.placeId === source.placeId
      : existing.name === source.name &&
        existing.lat === source.lat &&
        existing.lon === source.lon;
    if (same) return route;
  }

  const copy = makeWaypoint({
    kind,
    name: source.name,
    lat: source.lat,
    lon: source.lon,
    src: source.src,
    geocodedAt: source.geocodedAt,
    autoName: source.autoName,
    placeId: source.placeId,
    mapLabel: source.mapLabel,
  });
  if (!existing) return upsertAnchor(route, copy);
  return {
    v: ROUTE_VERSION,
    waypoints: route.waypoints.map((wp) =>
      wp.id === existing.id ? { ...copy, id: existing.id } : wp
    ),
  };
}

// The add-waypoint gesture landing on a gazetteer dot: insertViaNearSegment's
// refine-or-extend rule, but the via is BOUND -- named by the row, positioned
// by the row, tracking it afterwards.
//
// The tap's own coordinates appear nowhere, including in the snap test: the row
// is where the via goes, so the row is where "is this on the line?" has to be
// measured. Callers with no on-screen claim to honour (a popup button rather
// than a tap) omit `snapNm`, which is insertViaNearSegment's own documented
// default for every non-map caller.
export function insertViaAtPlace(route, place, { snapNm = Infinity } = {}) {
  return spliceVia(route, waypointFromPlace('via', place), snapNm);
}
